import { useState, useEffect } from "react";
import { Modal, Input, Button } from "@marekl/adminer/components";
import {
  useCreateVoucherType,
  useUpdateVoucherType,
} from "../../../lib/queries/voucherTypes";
import type {
  VoucherType,
  CreateVoucherTypePayload,
  UpdateVoucherTypePayload,
} from "../../../lib/queries/voucherTypes";
import { notifySuccess, notifyError } from "@marekl/adminer/api";
import styles from "./VoucherTypeModal.module.css";

interface VoucherTypeModalProps {
  open: boolean;
  /** null = create mode, non-null = edit mode */
  voucherType: VoucherType | null;
  onClose: () => void;
}

interface FormState {
  code: string;
  name: string;
  default_rides: string;
  ride_length_min: string;
  price_czk: string;
  validity_months: string;
  active: boolean;
  low_stock_threshold: string;
  barcode_type_digits: string;
}

const EMPTY_FORM: FormState = {
  code: "",
  name: "",
  default_rides: "",
  ride_length_min: "10",
  price_czk: "",
  validity_months: "12",
  active: true,
  low_stock_threshold: "0",
  barcode_type_digits: "",
};

function vtToForm(vt: VoucherType): FormState {
  return {
    code: vt.code,
    name: vt.name,
    default_rides: String(vt.default_rides),
    ride_length_min: String(vt.ride_length_min),
    price_czk: String(vt.price_czk),
    validity_months: String(vt.validity_months),
    active: vt.active,
    low_stock_threshold: String(vt.low_stock_threshold),
    barcode_type_digits: vt.barcode_type_digits ?? "",
  };
}

export function VoucherTypeModal({ open, voucherType, onClose }: VoucherTypeModalProps) {
  const isEdit = voucherType !== null;
  const createVT = useCreateVoucherType();
  const updateVT = useUpdateVoucherType(voucherType?.id ?? 0);
  const isPending = createVT.isPending || updateVT.isPending;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  useEffect(() => {
    if (open) {
      setForm(voucherType ? vtToForm(voucherType) : EMPTY_FORM);
      setErrors({});
    }
  }, [open, voucherType]);

  function field<K extends keyof FormState>(key: K) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));
  }

  function validate(): boolean {
    const e: typeof errors = {};
    if (!isEdit && !form.code.trim()) e.code = "Povinné pole";
    if (!form.name.trim()) e.name = "Povinné pole";
    const rides = Number(form.default_rides);
    if (!Number.isInteger(rides) || rides < 0) e.default_rides = "Nezáporné celé číslo";
    const len = Number(form.ride_length_min);
    if (!Number.isInteger(len) || len < 0) e.ride_length_min = "Nezáporné celé číslo";
    const price = Number(form.price_czk);
    if (!Number.isInteger(price) || price < 0) e.price_czk = "Nezáporné celé číslo";
    const validity = Number(form.validity_months);
    if (!Number.isInteger(validity) || validity < 1) e.validity_months = "Alespoň 1 měsíc";
    const threshold = Number(form.low_stock_threshold);
    if (!Number.isInteger(threshold) || threshold < 0) e.low_stock_threshold = "Nezáporné celé číslo";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    try {
      if (isEdit) {
        const payload: UpdateVoucherTypePayload = {
          name: form.name.trim(),
          default_rides: Number(form.default_rides),
          ride_length_min: Number(form.ride_length_min),
          price_czk: Number(form.price_czk),
          validity_months: Number(form.validity_months),
          active: form.active,
          low_stock_threshold: Number(form.low_stock_threshold),
          barcode_type_digits: form.barcode_type_digits.trim() || null,
        };
        await updateVT.mutateAsync(payload);
        notifySuccess("Typ voucheru byl upraven.");
      } else {
        const payload: CreateVoucherTypePayload = {
          code: form.code.trim().toUpperCase(),
          name: form.name.trim(),
          default_rides: Number(form.default_rides),
          ride_length_min: Number(form.ride_length_min),
          price_czk: Number(form.price_czk),
          validity_months: Number(form.validity_months),
          active: form.active,
          low_stock_threshold: Number(form.low_stock_threshold),
          barcode_type_digits: form.barcode_type_digits.trim() || null,
        };
        await createVT.mutateAsync(payload);
        notifySuccess("Typ voucheru byl vytvořen.");
      }
      onClose();
    } catch (err) {
      notifyError(err);
    }
  }

  const title = isEdit
    ? `Upravit: ${voucherType!.name}`
    : "Nový typ voucheru";

  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      width={600}
      footer={
        <>
          <Button onClick={onClose}>Zrušit</Button>
          <Button
            variant="primary"
            loading={isPending}
            onClick={handleSubmit as unknown as React.MouseEventHandler}
          >
            {isEdit ? "Uložit" : "Vytvořit"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.grid}>
          <Input
            id="vt-code"
            label="Kód *"
            value={form.code}
            onChange={field("code")}
            error={errors.code}
            disabled={isEdit}
            autoComplete="off"
            spellCheck={false}
            className={styles.mono}
          />
          <Input
            id="vt-name"
            label="Název *"
            value={form.name}
            onChange={field("name")}
            error={errors.name}
            autoComplete="off"
          />
          <Input
            id="vt-rides"
            label="Počet jízd"
            type="number"
            min={0}
            value={form.default_rides}
            onChange={field("default_rides")}
            error={errors.default_rides}
          />
          <Input
            id="vt-ride-len"
            label="Délka jízdy (min)"
            type="number"
            min={0}
            value={form.ride_length_min}
            onChange={field("ride_length_min")}
            error={errors.ride_length_min}
          />
          <Input
            id="vt-price"
            label="Cena (Kč)"
            type="number"
            min={0}
            value={form.price_czk}
            onChange={field("price_czk")}
            error={errors.price_czk}
          />
          <Input
            id="vt-validity"
            label="Platnost (měsíce)"
            type="number"
            min={1}
            value={form.validity_months}
            onChange={field("validity_months")}
            error={errors.validity_months}
          />
          <Input
            id="vt-threshold"
            label="Práh nízkého stavu"
            type="number"
            min={0}
            value={form.low_stock_threshold}
            onChange={field("low_stock_threshold")}
            error={errors.low_stock_threshold}
          />
          <Input
            id="vt-barcode-type-digits"
            label="EAN prefix typu"
            placeholder="např. 2 nebo 2,6"
            value={form.barcode_type_digits}
            onChange={field("barcode_type_digits")}
            autoComplete="off"
            spellCheck={false}
            className={styles.mono}
          />
          <div className={styles.activeField}>
            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              />
              Aktivní
            </label>
          </div>
        </div>
      </form>
    </Modal>
  );
}
